import startCase from "lodash/startCase";
import uniqueId from "lodash/uniqueId";
import * as React from "react";
import { Link } from "react-router-dom";

import SummaryProvider from "@app/projects/network-assessment/summary/summaryProvider";
import { formatDate, formatPhoneNumber, PageSection, SearchBox } from "@app/shared";
import { SummaryResultNetworkType, SummaryResultScope, SummaryResultType } from "@app/shared/data-summaries";
import { QESAlert } from "@app/shared/qes-standards";
import { ProvidersResult } from "./providersResult";
import { SortDirections, Waffles, wafflesKeyColumnName } from "../waffles";
import { withZipProjectContext } from "@app/projects/zip-projects/ZipProjectContext";
import { CountyProjectContextProps, withCountyProjectContext } from "@app/projects/CountyProjectContext";
import { ZipContextState } from "@app/projects/zip-projects/interfaces";
import { JSX } from "react";

type ProviderListingProps = ZipContextState & CountyProjectContextProps & {
  getProviders: (skip: number, searchValue: string, sortKey: string, sortDirection: SortDirections) => Promise<ProvidersResult>;
  projectId: number;
  runConfigurationId: number;
  scope: SummaryResultScope;
  networkType: SummaryResultNetworkType;
  resultType: string;
  summaryKey: string;
  exportEndpoint: string;
  projectURL: string;
}

interface ProviderListingState {
  error: Error;
  loading: boolean;
  result: ProvidersResult;
  search: string;
  skip: number;
  sortDirection: SortDirections;
  sortKey: keyof ProviderListingTableData;
  sortedRecords: ProviderListingTableData[];
}

type ProviderListingTableData = {
  [wafflesKeyColumnName]: string;
  address: JSX.Element;
  dateDeactivated: JSX.Element;
  name: string;
  npi: string;
  phone: string;
  specialty: string;
};

/**
 * Displays a list of providers as well as provides a download link to retrieve the *entire* output.
 * Not designed to display more than a thousand.
 */
class ProviderListing extends React.PureComponent<ProviderListingProps, ProviderListingState> {
  /** A set of nulled out providers used when loading. Gives the impression of there being data. */
  private static readonly loadingProviders: SummaryProvider[] = [
    {
      address: "",
      address2: "",
      city: "",
      name: "",
      npi: "Loading...",
      phone: "",
      providerId: -1,
      specialty: "",
      state: "",
      zip: "",
    },
  ];

  private readonly searchId = uniqueId("summary-provider-listing-search-");
  private readonly take = 100;

  constructor(props: ProviderListingProps) {
    super(props);

    this.state = {
      error: null,
      loading: true,
      result: null,
      search: "",
      skip: 0,
      sortDirection: SortDirections.None,
      sortKey: undefined,
      sortedRecords: [],
    };
  }

  public async componentDidMount() {
    await this.loadProviders();
  }

  public async componentDidUpdate(prevProps: ProviderListingProps, prevState: ProviderListingState) {
    if (prevProps.getProviders !== this.props.getProviders) {
      await this.loadProviders();
    }

    if (
      prevState.search !== this.state.search ||
      prevState.sortKey !== this.state.sortKey ||
      prevState.sortDirection !== this.state.sortDirection
    ) {
      await this.loadProviders(true);
    } else if (prevState.skip !== this.state.skip) {
      await this.loadProviders(false);
    }
  }

  public render() {
    return (
      <>
        <h2 className="page-title"><i className="fa fa-user-md mr-2" />{this.scopeDisplay()} {startCase(this.props.networkType)} Providers</h2>

        <PageSection>
          <div className="row">
            <div className="col-12 col-sm-8">
              <p className="lead">
                Provider Detail - Providers {this.resultDisplay()}
              </p>
            </div>
          </div>
        </PageSection>

        {/* Provider Search */}
        <PageSection>
          <div className="col-12 col-lg-8 pl-0">
            <SearchBox
              descriptiveText={`This will search each provider's NPI, name, address, phone number, and specialty.`}
              fieldsetClass="d-flex"
              inputLabel="Provider Search"
              onSubmit={(value: string) => { this.setState({ search: value }) }}
              testId="provider-listing__search"
            />
          </div>
        </PageSection>

        {/* Provider List */}
        <PageSection>
          {this.renderList()}
        </PageSection>
      </>
    );
  }

  private renderList() {
    if (this.state.error) {
      return (
        <QESAlert type="danger" classNames="mt-3">
          <strong>Error!</strong> Something happened while processing your request, please try again later. If the problem persists, please contact support.
        </QESAlert>
      );
    }

    if (!this.state.loading && this.state.sortedRecords.length === 0) {
      return (
        <QESAlert type="info" classNames="mt-3">
          No providers could be found.
        </QESAlert>
      );
    }

    let showingString = `Showing ${this.state.sortedRecords.length.toLocaleString()} of `;
    if (this.state.search) {
      showingString += `${this.state.result?.searchCount.toLocaleString()} filtered providers, and `
    }
    showingString += `${this.state.result?.totalCount.toLocaleString()} total providers`;

    return (
      <div className="row">
        <div className="col-12">
          {!this.state.loading && (
            <p className="mb5">{showingString}</p>
          )}
          <Waffles<ProviderListingTableData>
            bulkActionBar={<span className="bulk-action-group">
              <Link
                to={{
                  pathname: this.props.exportEndpoint,
                  state: {
                    displayKey: this.state?.result && this.state.result.displayName ? this.state.result.displayName : "(unknown)",
                    exportSummaryProviderCount: this.state?.result?.totalCount || 0,
                    isSummary: true,
                    networkType: this.props.networkType,
                    projectResourceId: this.props.project.resource.resourceId,
                    projectResourceName: this.props.project.resource.name,
                    projectURL: this.props.projectURL,
                    resultType: this.props.resultType,
                    scope: this.props.scope,
                    summaryKey: this.props.summaryKey,
                  },
                }}
              >
                <i className="fa fa-cloud-download" /> Create Report
              </Link>
            </span>}
            columns={this.getColumnConfig()}
            customSort={this.defaultSort}
            data={this.state.sortedRecords}
            fullWidth={true}
            enablePagination={true}
            paginationProps={{
              changePage: (pageNumber: number) => { this.setState({ skip: (pageNumber - 1) * this.take }); },
              recordsPerPage: this.take,
              skip: this.state.skip,
              totalRecords: this.state.result?.searchCount || this.state.result?.totalCount,
            }}
            passThroughSorting={true}
            loading={this.state.loading}
            testId="provider-listing"
          />
        </div>
      </div>
    );
  }

  private buildTableData = (providers: SummaryProvider[]): ProviderListingTableData[] => {
    /* eslint-disable sort-keys */
    return providers.map((provider) => ({
      [wafflesKeyColumnName]: provider.providerId.toString(),
      npi: provider.npi,
      name: provider.name,
      address: (
        <div key={provider.address}>
          {provider.address ? <>{provider.address}<br /></> : null}
          {provider.address2 ? <>{provider.address2}<br /></> : null}
          {provider.city}
          {provider.city && (provider.state || provider.zip) ? ", " : null}
          {provider.state} {provider.zip}
        </div>
      ),
      phone: formatPhoneNumber(provider.phone),
      specialty: provider.specialty,
      dateDeactivated: <>{formatDate(provider.dateDeactivated)}</>
    }));
    /* eslint-enable */
  }

  private getColumnConfig = () => {
    const showDeactivated = !this.state.loading && this.props.summaryKey === "Deactivated";

    return {
      "address": {
        displayText: "Address",
        testId: "address",
      },
      "dateDeactivated": {
        displayText: "Date Deactivated",
        hidden: !showDeactivated,
        testId: "date-deactivated",
      },
      "name": {
        displayText: "Name",

        testId: "name",
      },
      "npi": {
        displayText: "NPI",
        testId: "npi",
      },
      "phone": {
        displayText: "Phone",
        testId: "phone",
      },
      "specialty": {
        displayText: "Specialty",
        testId: "specialty",
      },
    }
  }

  private scopeDisplay = () => {
    if (this.props.scope === SummaryResultScope.Network) {
      return "All";
    }
    return "Servicing";
  }

  private resultDisplay = () => {
    if (this.state.result && this.state.result.displayName) {
      switch (this.props.resultType) {
        case SummaryResultType.ByAddressCount: return `with ${this.state.result.displayName} Addresses`;
        case SummaryResultType.ByCounty: return `By County (${this.state.result.displayName})`;
        case SummaryResultType.ByGeocode: return `By Geocode (${this.state.result.displayName})`;
        case SummaryResultType.ByNPIStatus: return `By NPI Status (${this.state.result.displayName})`;
        case SummaryResultType.BySpecialty: return `By Specialty (${this.state.result.displayName})`;
        case SummaryResultType.BySpecialtyCount: return `with ${this.state.result.displayName} Specialties`;
        case SummaryResultType.ByState: return `By State (${this.state.result.displayName})`;
        default: return "Overall Summary";
      }
    }
    return null;

  }

  private async loadProviders(resetSkip?: boolean) {
    const providersPromise = this.props.getProviders(resetSkip ? 0 : this.state.skip, this.state.search, this.state.sortKey, this.state.sortDirection);
    this.setState({ error: null, loading: true }, async () => {
      try {
        const result = await providersPromise;
        this.setState({ loading: false, result, skip: resetSkip ? 0 : this.state.skip, sortedRecords: result ? this.buildTableData(result.providers) : [] });
      } catch (e) {
        this.setState({ error: e, loading: false });
      }
    });
  }

  private defaultSort = (data: ProviderListingTableData[], columnKey: keyof ProviderListingTableData, sortDirection: SortDirections) => {
    this.setState({
      loading: true,
      sortDirection: sortDirection,
      sortKey: columnKey,
    });
    return data;
  }
}

const ProviderListingWrapped = withZipProjectContext(withCountyProjectContext(ProviderListing));

export { ProviderListingWrapped as ProviderListing };
